import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import {
  TareaEjecucionDetalleProductoItem,
  TareaEjecucionItem,
  TareaEjecucionService,
} from "../../../generated/grpc/rastro_tarea_ejecucion_pb";
import { grpcTareaEjecucionDatabase } from "../database/GrpcTareaEjecucionDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { TareaEjecucionResponse } from "../response/TareaEjecucionResponse";
import { TareaEjecucionDetalleProductoResponse } from "../response/TareaEjecucionDetalleProductoResponse";

/** Item guardado localmente: `TareaEjecucionItem` (gRPC) + `fincaId` inyectado
 *  acá -no viaja en el mensaje (el cliente ya sabe qué finca pidió, mismo
 *  criterio que `EscenaGrpcService`)-, pero hace falta como propiedad real del
 *  registro para el índice `fincaId` de `GrpcTareaEjecucionDatabase`. */
export type StoredTareaEjecucionItem = TareaEjecucionItem & { fincaId: string };

/** Variante gRPC-Web (con delta real, por finca) de `TareaEjecucionGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `TareaEjecucionResponse[]` -mismo shape que ya
 *  usa el resto de la app-. Rellena con valores inertes los campos sin uso
 *  real (`fincaId`, `gpsTrackerId`, `areaHas` — ver el comentario de
 *  `TareaEjecucionItem` en el proto). Desde el 2026-09-30 el listado no trae
 *  los recorridos: los pide el mapa por día con
 *  `fx_getTareaEjecucionRecorridosAsync`. La sincronización del listado sigue
 *  siendo por delta por finca (`_syncAsync`), sin cambios. */
class TareaEjecucionGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<TareaEjecucionResponse[]> {
    await this._syncAsync();
    const items = await grpcTareaEjecucionDatabase.getListByFincaIdAsync<StoredTareaEjecucionItem>(
      this._fincaId,
    );
    return items.map(toTareaEjecucionResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:tareas-ejecucion:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    // Sin cooldown (2026-09-15, restaura el criterio original de
    // TareaEjecucionGetListByFincaIdService — REST): las ejecuciones las suben
    // los GPS Tracker todo el tiempo, así que siempre sincroniza (delta) al
    // pedir la lista. La migración a gRPC había caído en el default de 24h de
    // ServiceSync sin querer, al no pasar este tercer argumento.
    await serviceSync.run(`grpc:tareas-ejecucion:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcTareaEjecucionDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(TareaEjecucionService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      const items: StoredTareaEjecucionItem[] = response.items.map((item) => ({
        ...item,
        fincaId: this._fincaId,
      }));
      await grpcTareaEjecucionDatabase.putDeltaAsync(
        this._fincaId,
        items,
        response.timestampSincronizacion,
      );
    }, 0);
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createTareaEjecucionGetListByFincaIdService`). */
export const fx_createTareaEjecucionGrpcService = createCachedFactory(
  (fincaId: string) => new TareaEjecucionGrpcService(fincaId),
);

/** Adapta un `TareaEjecucionItem` (gRPC) al shape `TareaEjecucionResponse`
 *  (REST). Exportada porque los servicios de guardado/asignación también la
 *  necesitan. */
export function toTareaEjecucionResponse(
  ejecucion: TareaEjecucionItem,
): TareaEjecucionResponse {
  return {
    id: ejecucion.id,
    isEliminado: ejecucion.eliminado,
    fincaId: "",
    gpsTrackerId: "",
    gpsTrackerNumero: ejecucion.gpsTrackerNumero,
    tareaId: ejecucion.tareaId ?? null,
    tareaNombre: ejecucion.tareaNombre ?? null,
    fechaInicio: ejecucion.fechaInicio,
    fechaFin: ejecucion.fechaFin,
    ultimaActualizacion: Number(ejecucion.ultimaActualizacion),
    distanciaKm: ejecucion.distanciaKm ?? null,
    velocidadPromedioKmH: ejecucion.velocidadPromedioKmH ?? null,
    areaHas: null,
    muestrasEjecucion: ejecucion.muestrasEjecucion ?? null,
    detalles: ejecucion.detalles.map(toDetalleResponse),
  };
}

function toDetalleResponse(
  detalle: TareaEjecucionDetalleProductoItem,
): TareaEjecucionDetalleProductoResponse {
  return {
    productoId: detalle.productoId,
    productoDescripcion: detalle.productoDescripcion,
    unidadMedida: detalle.unidadMedida,
    dosis: detalle.dosis,
  };
}
